import { contactColumn, FlowTemplate, sectionIcon, singleColumnPage, type RenderContext, type Zone } from "../base";
import { col, expanded, hspace, keep, linkText, padding, rect, rich, row, sized, text, vspace, type Node, type PageSpec } from "../ir";
import { hasCategories, skillGroups, type RenderEntry, type RenderSection } from "../model";

/**
 * Prestige — refined and quiet: a Playfair Display name, letter-spaced title,
 * hairline rules and italic serif headings over a clean sans body.
 * (prestige_template.dart)
 */
export class PrestigeTemplate extends FlowTemplate {
  readonly info = {
    id: "prestige",
    name: "Prestige",
    tagline: "Elegant serif headings with hairline detail",
    category: "executive" as const,
    layout: "One column",
    defaultAccent: 0xff8a6a3b,
    atsFriendly: true,
    supportsPhoto: false,
  };

  readonly fonts = {
    body: "lato" as const,
    heading: "playfair" as const,
    display: "playfair" as const,
    headingFollowsBody: false,
    displayFollowsBody: false,
  };

  zoneWidth(c: RenderContext): number {
    return c.format.width - 100;
  }

  heading(c: RenderContext, s: RenderSection): Node {
    const t = c.t;
    const icon = sectionIcon(c, s, t.accentText, t.fs(12.5));
    return row([
      icon,
      icon && hspace(7),
      text(s.title, t.style(t.heading.italic, 13.5, { color: t.accentText, lineSpacing: 0 })),
      hspace(12),
      expanded(rect({ height: 0.5, color: t.accentSoft })),
    ]);
  }

  bulletGlyph(c: RenderContext, z: Zone): Node {
    return padding({ top: this.bulletStyle(c, z).size * 0.55 }, rect({ width: 5, height: 0.8, color: c.t.accent }));
  }

  entryHeader(c: RenderContext, e: RenderEntry): Node {
    const t = c.t;
    const title = e.title || e.subtitle;
    const subtitle = e.title ? e.subtitle : "";
    return col([
      row(
        [
          expanded(linkText(title, t.style(t.body.bold, 10, { color: t.ink, lineSpacing: 0 }), e.url)),
          e.dates &&
            text(e.dates.toUpperCase(), t.style(t.body.regular, 7.6, { color: t.muted, letterSpacing: 1, lineSpacing: 0 })),
        ],
        { align: "end" }
      ),
      (subtitle || e.meta) &&
        padding(
          { top: 2 },
          rich([
            subtitle ? [subtitle, t.style(t.heading.italic, 10, { color: t.text })] : null,
            subtitle && e.meta ? ["   ", t.style(t.body.regular, 8)] : null,
            e.meta ? [e.meta, t.style(t.body.regular, 8.4, { color: t.muted })] : null,
          ])
        ),
    ]);
  }

  chips(c: RenderContext, items: string[]): Node {
    return text(items.join("   ·   "), c.t.style(c.t.body.regular, 9, { color: c.t.text }));
  }

  skillsContent(c: RenderContext, s: RenderSection, z: Zone): Node[] {
    if (c.showSkillLevels) return this.levelList(c, s.skills, z, { grouped: hasCategories(s) });
    const t = c.t;
    return skillGroups(s).map(([key, list]) =>
      padding(
        { bottom: this.lineGap(c, z) + 1.5 },
        key
          ? row(
              [
                sized(110, undefined, text(key, t.style(t.heading.italic, 9.6, { color: t.ink }))),
                expanded(this.chips(c, list.map((x) => x.name))),
              ],
              { align: "start" }
            )
          : this.chips(c, list.map((x) => x.name))
      )
    );
  }

  build(c: RenderContext): PageSpec {
    const t = c.t;
    const m = c.model;
    const header = col(
      [
        row(
          [
            expanded(
              col([
                m.name && text(m.name, t.style(t.display.regular, 29, { color: t.ink, letterSpacing: 0.3, lineSpacing: 0 })),
                m.headline && vspace(6),
                m.headline &&
                  text(m.headline.toUpperCase(), t.style(t.body.regular, 8.8, { color: t.accentText, letterSpacing: 3, lineSpacing: 1 })),
              ])
            ),
            m.contacts.length > 0 && hspace(18),
            m.contacts.length > 0 &&
              col(
                contactColumn(c, {
                  style: t.style(t.body.regular, 8.2, { color: t.text, lineSpacing: 0 }),
                  iconColor: t.accent,
                  gap: 3.5,
                  alignRight: true,
                }),
                { width: 175, align: "end" }
              ),
          ],
          { align: "end" }
        ),
        vspace(14),
        row([rect({ width: 64, height: 1, color: t.accent }), expanded(rect({ height: 0.4, color: t.accentSoft }))]),
        vspace(t.gap(20)),
      ],
      { align: "center" }
    );
    return singleColumnPage(c, [50, 46, 50, 32], [keep([header]), ...this.allSections(c, m.sections)]);
  }
}
